import {
  ChangeDetectionStrategy,
  Component,
  computed,
  input,
  output,
  signal,
  WritableSignal,
} from '@angular/core';
import { form, FormField } from '@angular/forms/signals';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatListModule } from '@angular/material/list';
import { AgentCardFilterKey, FrenchDepartment, ZoudashUtils } from '@workspace/data-types';
import {
  AbstractSignalFormComponent,
  DataHeaderFilter,
  FilterChangedDetection,
  FilterRecord,
} from '@workspace/ng-aura';
import { AgentCardColumnKey } from '../abstract-agent-cards-data-table.component';

export interface AgentCardFilterModel {
  mentions: string | string[] | null;
  departmentCode: string | string[] | null;
}

@Component({
  selector: 'etxe-agent-cards-data-table-filter',
  templateUrl: './agent-cards-data-table-filter.component.html',
  imports: [FormField, MatListModule, MatFormFieldModule, MatInputModule, MatIconModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AgentCardsDataTableFilterComponent extends AbstractSignalFormComponent<
  Partial<Record<AgentCardColumnKey, string | string[]>>,
  AgentCardFilterModel
> {
  filterChanged = output<FilterChangedDetection>();
  currentFilterView = input<string | null>(null);
  filters = input.required<DataHeaderFilter<AgentCardColumnKey>[]>();

  agentCardFilterKey = AgentCardFilterKey;

  readonly mentionOptions = [
    { value: 'transaction', label: 'Transaction' },
    { value: 'management', label: 'Gestion' },
    { value: 'syndic', label: 'Syndic' },
    { value: 'listBroker', label: 'Marchand de listes' },
    { value: 'tourism', label: 'Prestations touristiques' },
    { value: 'service', label: 'Prestations de services' },
  ];

  readonly departmentSearch = signal('');

  readonly allDepartments = Object.entries(FrenchDepartment)
    .map(([key, code]) => ({
      code,
      name: key
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' '),
      label: `${code} - ${key
        .split('_')
        .map((w) => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
        .join(' ')}`,
    }))
    .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));

  readonly filteredDepartments = computed(() => {
    const search = this.departmentSearch().toLowerCase().trim();
    if (!search) return this.allDepartments;
    return this.allDepartments.filter(
      (dept) => dept.code.toLowerCase().includes(search) || dept.name.toLowerCase().includes(search),
    );
  });

  override model: WritableSignal<AgentCardFilterModel> = signal<AgentCardFilterModel>({
    mentions: null,
    departmentCode: null,
  });

  override formTree = form(this.model);

  override emitDto(newDto: Partial<Record<AgentCardColumnKey, string | string[]>>): void {
    this.filterChanged.emit({
      filter: newDto as FilterRecord,
      changed: !ZoudashUtils.isEqual(newDto, this.ogValue),
    });
  }

  protected override buildModelFromDto(
    dto: Partial<Record<AgentCardColumnKey, string | string[]>>,
  ): AgentCardFilterModel {
    return {
      mentions: (dto?.[AgentCardFilterKey.Mentions as AgentCardColumnKey] as string | string[]) ?? null,
      departmentCode:
        (dto?.[AgentCardFilterKey.DepartmentCode as AgentCardColumnKey] as string | string[]) ??
        (dto?.['departmentCode' as AgentCardColumnKey] as string | string[]) ??
        null,
    };
  }

  protected override buildDtoFromModel(
    model: AgentCardFilterModel,
  ): Partial<Record<AgentCardColumnKey, string | string[]>> {
    const dto: Partial<Record<string, string | string[]>> = {
      [AgentCardFilterKey.Mentions]: model.mentions ?? undefined,
      [AgentCardFilterKey.DepartmentCode]: model.departmentCode ?? undefined,
    };
    return Object.fromEntries(Object.entries(dto).filter(([, v]) => v != null)) as Partial<
      Record<AgentCardColumnKey, string | string[]>
    >;
  }

  onDepartmentSearchInput(event: Event): void {
    const input = event.target as HTMLInputElement;
    this.departmentSearch.set(input.value || '');
  }
}
